import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Animal3DCanvas } from "./Animal3DCanvas";
import type { AnimalType } from "./Animal3DModel";
import {
  useDressupStore,
  type HeadwearType,
  type OutfitType,
  type AccessoryType,
  type ColorTintType,
} from "../../store/dressup";

type TabCategory = "animal" | "headwear" | "outfit" | "accessory" | "color";

interface ItemOption<T> {
  id: T;
  nameJa: string;
  nameEn: string;
  icon: string;
  previewColor?: string;
}

const ANIMALS: Array<{ id: AnimalType; nameJa: string; emoji: string }> = [
  { id: "bunny", nameJa: "うさぎ", emoji: "🐰" },
  { id: "frog", nameJa: "カエル", emoji: "🐸" },
  { id: "tanuki", nameJa: "タヌキ", emoji: "🍃" },
  { id: "kitsune", nameJa: "キツネ", emoji: "🦊" },
  { id: "monkey", nameJa: "サル", emoji: "🐒" },
];

const HEADWEAR_ITEMS: ItemOption<HeadwearType>[] = [
  { id: "none", nameJa: "なし", nameEn: "None", icon: "🚫" },
  { id: "straw_hat", nameJa: "麦わら帽子", nameEn: "Straw Hat", icon: "👒" },
  { id: "top_hat", nameJa: "シルクハット", nameEn: "Top Hat", icon: "🎩" },
  { id: "beret", nameJa: "ベレー帽", nameEn: "Beret", icon: "🎨" },
  { id: "crown", nameJa: "王冠", nameEn: "Crown", icon: "👑" },
  { id: "flower", nameJa: "お花の髪飾り", nameEn: "Flower", icon: "🌸" },
];

const OUTFIT_ITEMS: ItemOption<OutfitType>[] = [
  { id: "default", nameJa: "普段着", nameEn: "Default", icon: "👕" },
  { id: "tie", nameJa: "ネクタイ", nameEn: "Red Tie", icon: "👔" },
  { id: "apron", nameJa: "カフェエプロン", nameEn: "Apron", icon: "☕" },
  { id: "cape", nameJa: "ヒーローマント", nameEn: "Cape", icon: "🦸" },
  { id: "bandana", nameJa: "唐草バンダナ", nameEn: "Bandana", icon: "🧣" },
  { id: "bowtie", nameJa: "蝶ネクタイ", nameEn: "Bowtie", icon: "🎀" },
];

const ACCESSORY_ITEMS: ItemOption<AccessoryType>[] = [
  { id: "none", nameJa: "なし", nameEn: "None", icon: "🚫" },
  { id: "glasses", nameJa: "知的な丸メガネ", nameEn: "Glasses", icon: "👓" },
  { id: "sunglasses", nameJa: "クールサングラス", nameEn: "Sunglasses", icon: "🕶️" },
  { id: "backpack", nameJa: "おでかけリュック", nameEn: "Backpack", icon: "🎒" },
  { id: "camera", nameJa: "レトロカメラ", nameEn: "Camera", icon: "📷" },
];

const COLOR_ITEMS: ItemOption<ColorTintType>[] = [
  { id: "default", nameJa: "オリジナル", nameEn: "Original", icon: "⭐", previewColor: "#f3e8ff" },
  { id: "sakura", nameJa: "桜ピンク", nameEn: "Sakura", icon: "🌸", previewColor: "#ffd6e0" },
  { id: "latte", nameJa: "カフェラテ", nameEn: "Latte", icon: "☕", previewColor: "#d7bfa8" },
  { id: "mint", nameJa: "ミントグリーン", nameEn: "Mint", icon: "🌿", previewColor: "#c1e8de" },
  { id: "snow", nameJa: "スノーホワイト", nameEn: "Snow", icon: "❄️", previewColor: "#ffffff" },
];

export function DressupStudio({ onClose }: { onClose?: () => void }) {
  const store = useDressupStore();
  const [activeTab, setActiveTab] = useState<TabCategory>("headwear");
  const [celebrateMsg, setCelebrateMsg] = useState<string>("");

  function triggerFeedback(itemLabel: string) {
    setCelebrateMsg(`「${itemLabel}」に着替えたよ！✨`);
    setTimeout(() => setCelebrateMsg(""), 2400);
  }

  return (
    <div className="bg-[#fffdf8] rounded-3xl p-4 sm:p-6 shadow-2xl border-2 border-[#d9b45f]/40 max-w-2xl w-full mx-auto flex flex-col">
      {/* スタジオヘッダー */}
      <div className="flex items-center justify-between pb-3 border-b border-[#d9b45f]/20">
        <div className="flex items-center gap-2.5">
          <span className="text-2xl sm:text-3xl">👗</span>
          <div>
            <h2 className="text-lg sm:text-xl font-black text-[#8f332c] leading-tight">
              どうぶつクローゼット
            </h2>
            <p className="text-xs text-stone-500 font-bold">
              お気に入りのどうぶつを着せ替えて島を冒険しよう！
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 flex items-center justify-center text-sm font-bold transition active:scale-90"
          >
            ✕
          </button>
        )}
      </div>

      {/* メインエリア：上部/左に3Dプレビュー、下部/右にパーツセレクタ */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4">
        {/* 3Dプレビュー */}
        <div className="md:col-span-6 flex flex-col items-center">
          <div className="relative w-full rounded-2xl bg-gradient-to-b from-sky-50 via-amber-50/40 to-pink-50/50 border-2 border-[#d9b45f]/30 p-2 overflow-hidden shadow-inner">
            <Animal3DCanvas
              height={260}
              allowOrbit={true}
              autoWave={true}
            />

            {/* 着替え完了メッセージ吹き出し */}
            <AnimatePresence>
              {celebrateMsg && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/95 border-2 border-[#8f332c] text-[#8f332c] font-black text-xs px-3.5 py-1.5 rounded-full shadow-lg whitespace-nowrap z-20"
                >
                  {celebrateMsg}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* 動物切り替えセレクタ */}
          <div className="flex gap-1.5 mt-3 w-full bg-stone-100/90 p-1.5 rounded-2xl">
            {ANIMALS.map((a) => {
              const selected = store.animalType === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => {
                    store.setAnimalType(a.id);
                    triggerFeedback(a.nameJa);
                  }}
                  className={`flex-1 py-1.5 rounded-xl text-xs font-black transition flex items-center justify-center gap-1 ${
                    selected
                      ? "bg-white text-[#8f332c] shadow-sm border border-[#d9b45f]/40 scale-102"
                      : "text-stone-500 hover:text-stone-800"
                  }`}
                >
                  <span className="text-base">{a.emoji}</span>
                  <span className="hidden sm:inline">{a.nameJa}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 右側：カテゴリータブ ＆ アイテム選択グリッド */}
        <div className="md:col-span-6 flex flex-col">
          {/* カテゴリタブ */}
          <div className="flex gap-1 border-b border-stone-200 pb-1.5 overflow-x-auto">
            {(
              [
                { id: "headwear", label: "あたま", emoji: "🎩" },
                { id: "outfit", label: "お洋服", emoji: "👕" },
                { id: "accessory", label: "こもの", emoji: "👓" },
                { id: "color", label: "カラー", emoji: "🎨" },
              ] as const
            ).map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-black transition flex items-center gap-1 whitespace-nowrap ${
                    active
                      ? "bg-[#8f332c] text-white shadow-sm"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                >
                  <span>{tab.emoji}</span>
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* アイテムグリッド */}
          <div className="grid grid-cols-3 gap-2 mt-3 overflow-y-auto max-h-[220px] p-1 pr-2 scrollbar-thin">
            {activeTab === "headwear" &&
              HEADWEAR_ITEMS.map((item) => {
                const selected = store.headwear === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      store.setHeadwear(item.id);
                      triggerFeedback(item.nameJa);
                    }}
                    className={`p-2.5 rounded-2xl border-2 transition flex flex-col items-center justify-center text-center gap-1 ${
                      selected
                        ? "bg-amber-50/80 border-[#8f332c] shadow-sm scale-102"
                        : "bg-white border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-[11px] font-bold text-stone-800 leading-tight">
                      {item.nameJa}
                    </span>
                  </button>
                );
              })}

            {activeTab === "outfit" &&
              OUTFIT_ITEMS.map((item) => {
                const selected = store.outfit === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      store.setOutfit(item.id);
                      triggerFeedback(item.nameJa);
                    }}
                    className={`p-2.5 rounded-2xl border-2 transition flex flex-col items-center justify-center text-center gap-1 ${
                      selected
                        ? "bg-amber-50/80 border-[#8f332c] shadow-sm scale-102"
                        : "bg-white border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-[11px] font-bold text-stone-800 leading-tight">
                      {item.nameJa}
                    </span>
                  </button>
                );
              })}

            {activeTab === "accessory" &&
              ACCESSORY_ITEMS.map((item) => {
                const selected = store.accessory === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      store.setAccessory(item.id);
                      triggerFeedback(item.nameJa);
                    }}
                    className={`p-2.5 rounded-2xl border-2 transition flex flex-col items-center justify-center text-center gap-1 ${
                      selected
                        ? "bg-amber-50/80 border-[#8f332c] shadow-sm scale-102"
                        : "bg-white border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-[11px] font-bold text-stone-800 leading-tight">
                      {item.nameJa}
                    </span>
                  </button>
                );
              })}

            {activeTab === "color" &&
              COLOR_ITEMS.map((item) => {
                const selected = store.colorTint === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      store.setColorTint(item.id);
                      triggerFeedback(item.nameJa);
                    }}
                    className={`p-2.5 rounded-2xl border-2 transition flex flex-col items-center justify-center text-center gap-1 ${
                      selected
                        ? "bg-amber-50/80 border-[#8f332c] shadow-sm scale-102"
                        : "bg-white border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    <div
                      className="w-6 h-6 rounded-full border border-stone-300 shadow-inner flex items-center justify-center text-xs"
                      style={{ backgroundColor: item.previewColor ?? "#fff" }}
                    >
                      {item.icon}
                    </div>
                    <span className="text-[11px] font-bold text-stone-800 leading-tight">
                      {item.nameJa}
                    </span>
                  </button>
                );
              })}
          </div>

          {/* フッターアクション（デフォルトに戻す） */}
          <div className="mt-auto pt-3 flex items-center justify-between border-t border-stone-200">
            <button
              onClick={() => {
                store.resetToDefault();
                triggerFeedback("デフォルト");
              }}
              className="text-xs font-bold text-stone-500 hover:text-[#8f332c] transition underline"
            >
              初期状態に戻す
            </button>
            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full font-bold">
              ✓ 自動保存中
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
